import React, { useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { Document, Page, pdfjs } from "react-pdf";
import { FaList, FaFilePdf } from "react-icons/fa";
import styles from "./Pdfmodal.module.css";

// Set worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

/**
 * PDFModal - Reusable PDF Viewer Modal
 *
 * @param {boolean} show - Controls modal visibility (from parent)
 * @param {function} onClose - Callback to close modal (from parent)
 * @param {string} pdfUrl - URL of the PDF to display
 * @param {string} modalTitle - Title shown in the modal header (optional)
 * @param {object} translations - Translation object for i18n (optional)
 */
const PDFModal = ({
  show = false,
  onClose,
  pdfUrl = "",
  modalTitle = "",
  translations = {},
}) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(0.5);
  const [pageLoadError, setPageLoadError] = useState(false);

  const t = translations?.labels || {};

  const onDocumentLoadSuccess = useCallback(({ numPages: total }) => {
    setNumPages(total);
    setCurrentPage(1);
    setPageLoadError(false);
  }, []);

  const onDocumentLoadError = useCallback((error) => {
    console.error("PDF load error:", error);
    setPageLoadError(true);
  }, []);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, numPages || 1));
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.25));
  };

  const handleClose = () => {
    setCurrentPage(1);
    setScale(0.5);
    setNumPages(null);
    setPageLoadError(false);
    if (onClose) onClose();
  };

  const zoomPercentage = Math.round(scale * 100);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      dialogClassName={styles.modalDialog}
    >
      {/* HEADER with toolbar and close */}
      <Modal.Header className={styles.modalHeader}>
        <div className={styles.headerLeft}>
          <Modal.Title className={styles.modalTitle}>
            {modalTitle || t?.viewUploadedBook || "View Uploaded Book"}
          </Modal.Title>
        </div>

        {/* Navigation & Zoom Controls */}
        {numPages && (
          <div className={styles.headerControls}>
            {/* Page Input */}
            <div className={styles.pageInputWrapper}>
              <input
                type="number"
                className={styles.pageInput}
                value={currentPage}
                min={1}
                max={numPages}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (val >= 1 && val <= numPages) {
                    setCurrentPage(val);
                  }
                }}
              />
              <span className={styles.pageTotal}>/ {numPages}</span>
            </div>

            {/* Zoom Controls */}
            <button
              className={styles.toolbarBtn}
              onClick={handleZoomOut}
              disabled={scale <= 0.25}
              title="Zoom Out"
            >
              −
            </button>
            <span className={styles.zoomLevel}>{zoomPercentage}%</span>
            <button
              className={styles.toolbarBtn}
              onClick={handleZoomIn}
              disabled={scale >= 3}
              title="Zoom In"
            >
              +
            </button>
          </div>
        )}

        {/* Close Button */}
        <button className={styles.closeBtn} onClick={handleClose}>
          {t?.close || "Close"}
        </button>
      </Modal.Header>

      {/* BODY with PDF */}
      <Modal.Body className={styles.modalBody}>
        <div className={styles.pdfViewerWrapper}>
          {pdfUrl ? (
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className={styles.pdfLoading}>
                  {t?.loading || "Loading PDF..."}
                </div>
              }
              error={
                <div className={styles.pdfError}>
                  {t?.failedToLoadPdf || "Failed to load PDF"}
                </div>
              }
            >
              <Page
                pageNumber={currentPage}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading={
                  <div className={styles.pdfLoading}>
                    {t?.loading || "Loading page..."}
                  </div>
                }
              />
            </Document>
          ) : (
            <div className={styles.pdfPlaceholder}>
              <div className={styles.pdfPlaceholderIcons}>
                <FaList size={32} color="#adb5bd" />
                <FaFilePdf size={32} color="#adb5bd" />
              </div>
              <p className={styles.pdfPlaceholderText}>
                {t?.failedToLoadPdf || "No PDF to display"}
              </p>
            </div>
          )}
        </div>

        {/* Bottom Navigation */}
        {numPages && (
          <div className={styles.bottomNav}>
            <button
              className={styles.navBtn}
              onClick={handlePrevPage}
              disabled={currentPage <= 1}
            >
              {t?.prev || "Prev"}
            </button>
            <span className={styles.pageInfo}>
              {t?.page || "Page"} {currentPage} {t?.of || "of"} {numPages}
            </span>
            <button
              className={styles.navBtn}
              onClick={handleNextPage}
              disabled={currentPage >= numPages}
            >
              {t?.next || "Next"}
            </button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PDFModal;
